import * as React from 'react'
import { create } from '@bufbuild/protobuf'
import { Car, Loader2, MapPin, Route } from 'lucide-react'
import { Link } from '@tanstack/react-router'

import {
  CalculateTransportationMileageRequestSchema,
  TransportationMileageDestinationSchema,
  type CalculateTransportationMileageResponse,
} from '@/gen/cortado/v1/cortado_pb'
import type { Item, TaxRate } from '@/gen/cortado/v1/invoices_pb'
import { useCalculateTransportationMileage, useTransportationItem } from '@/stores/transportation.store'
import { dollarsToCents, formatCurrency } from '@/lib/currency-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { applyTransportationMileageToLineItems } from '@/lib/transportation-line-items'
import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'

export type TransportationMileageEvent = {
  key: string
  label: string
  location?: string
}

type TransportationMileageDialogProps = {
  organizationId: string
  events: TransportationMileageEvent[]
  items: QuoteLineItem[]
  taxRates?: TaxRate[]
  autoApplyTaxRateId?: string
  disabled?: boolean
  triggerLabel?: string
  onItemsChange: (items: QuoteLineItem[]) => void
}

export function TransportationMileageDialog(props: TransportationMileageDialogProps) {
  const { organizationId, events, disabled, triggerLabel = 'Add Transportation' } = props
  const { item, isLoading } = useTransportationItem(organizationId)
  const [open, setOpen] = React.useState(false)
  // Equivalent parent renders retain the session; changed destinations or
  // organization settings start a new one and invalidate its pending requests.
  const sessionKey = JSON.stringify([
    organizationId, item?.id, item?.price, item?.mileageThresholdMiles,
    events.map((event) => [event.key, event.location?.trim() || '']),
  ])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled || isLoading}>
          <Car className="mr-2 size-4" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Transportation mileage</DialogTitle>
          <DialogDescription>
            Calculate driving distance from your organization address to the selected event locations.
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <TransportationMileageSession
            key={sessionKey}
            {...props}
            item={item}
            onClose={() => setOpen(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function TransportationMileageSession({
  organizationId,
  events,
  items,
  taxRates = [],
  autoApplyTaxRateId,
  disabled,
  onItemsChange,
  item,
  onClose,
}: TransportationMileageDialogProps & { item?: Item; onClose: () => void }) {
  const calculate = useCalculateTransportationMileage()
  const [roundTrip, setRoundTrip] = React.useState(true)
  const validEvents = events.filter((event) => !!event.location?.trim())
  const [selectedKeys, setSelectedKeys] = React.useState(() => new Set(validEvents.map((event) => event.key)))
  const [result, setResult] = React.useState<CalculateTransportationMileageResponse>()
  const [error, setError] = React.useState<string>()
  const requestVersion = React.useRef(0)

  React.useEffect(() => () => { requestVersion.current += 1 }, [])

  const invalidateCalculation = () => {
    requestVersion.current += 1
    setResult(undefined)
    setError(undefined)
  }

  const selectedEvents = validEvents.filter((event) => selectedKeys.has(event.key))
  const existingTransportationLines = item
    ? items.filter((lineItem) => lineItem.sourceItemId === item.id)
    : []
  const unitPriceCents = existingTransportationLines[0]?.unitPriceCents ?? dollarsToCents(item?.price ?? 0)

  const handleCalculate = async () => {
    if (!item || disabled || selectedEvents.length === 0) return
    const version = ++requestVersion.current
    setError(undefined)
    try {
      const response = await calculate.mutateAsync(create(CalculateTransportationMileageRequestSchema, {
        organizationId,
        transportationItemId: item.id,
        roundTrip,
        destinations: selectedEvents.map((event) => create(TransportationMileageDestinationSchema, {
          clientKey: event.key,
          location: event.location?.trim() || '',
        })),
      }))
      if (requestVersion.current === version) setResult(response)
    } catch (calculationError) {
      if (requestVersion.current === version) setError(getErrorDescription(calculationError))
    }
  }

  const handleApply = () => {
    if (!item || disabled || !result?.complete || existingTransportationLines.length > 1) return

    onItemsChange(applyTransportationMileageToLineItems({
      items,
      transportationItem: item,
      billableMiles: result.billableMiles,
      taxRates,
      autoApplyTaxRateId,
    }))
    onClose()
  }

  return (
    <>
      {!item ? (
        <Alert>
          <Car className="size-4" />
          <AlertTitle>Transportation is not configured</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>Set a price per mile and free-mile threshold before adding this charge.</p>
            <Button asChild size="sm" variant="outline">
              <Link to="/services" search={{ section: 'catalog' }}>Configure Transportation</Link>
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="font-medium">Round trip</p>
              <p className="text-sm text-muted-foreground">Double each one-way driving route.</p>
            </div>
            <Switch checked={roundTrip} disabled={disabled} onCheckedChange={(checked) => {
              invalidateCalculation()
              setRoundTrip(checked)
            }} aria-label="Round trip" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Events</Label>
              <Badge variant="secondary">{selectedEvents.length} selected</Badge>
            </div>
            <div className="max-h-56 space-y-2 overflow-y-auto rounded-lg border p-2">
              {events.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">Add an event before calculating mileage.</p>
              ) : events.map((event) => {
                const hasLocation = !!event.location?.trim()
                return (
                  <label key={event.key} className="flex items-start gap-3 rounded-md p-2 hover:bg-muted/50">
                    <Checkbox
                      checked={selectedKeys.has(event.key)}
                      disabled={disabled || !hasLocation}
                      onCheckedChange={(checked) => {
                        invalidateCalculation()
                        setSelectedKeys((current) => {
                          const next = new Set(current)
                          if (checked) next.add(event.key)
                          else next.delete(event.key)
                          return next
                        })
                      }}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{event.label}</span>
                      <span className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                        <MapPin className="size-3 shrink-0" />
                        {hasLocation ? event.location : 'Location required'}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Could not calculate mileage</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {result ? (
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-center gap-2 font-medium">
                <Route className="size-4" />
                Route summary
              </div>
              <div className="space-y-2 text-sm">
                {result.routes.map((route) => (
                  <div key={route.clientKey} className="flex justify-between gap-4">
                    <span className="truncate text-muted-foreground">{route.location}</span>
                    <span className={route.error ? 'text-destructive' : 'tabular-nums'}>
                      {route.error || `${route.tripMiles.toFixed(1)} mi`}
                    </span>
                  </div>
                ))}
              </div>
              {result.complete ? (
                <div className="space-y-1 border-t pt-3 text-sm">
                  <div className="flex justify-between">
                    <span>Total mileage</span>
                    <span className="font-medium tabular-nums">{result.totalMiles.toFixed(1)} mi</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Free threshold</span>
                    <span className="tabular-nums">{result.thresholdMiles.toFixed(1)} mi</span>
                  </div>
                  <div className="flex justify-between pt-1 font-medium">
                    <span>{result.billableMiles > 0 ? 'Transportation charge' : 'No transportation charge'}</span>
                    <span>
                      {result.billableMiles > 0
                        ? `${result.billableMiles.toFixed(1)} mi × $${formatCurrency(unitPriceCents)}`
                        : 'Threshold covers this trip'}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="border-t pt-3 text-sm text-destructive">
                  Fix the locations above before applying Transportation.
                </p>
              )}
            </div>
          ) : null}

          {existingTransportationLines.length > 1 ? (
            <Alert variant="destructive">
              <AlertTitle>Duplicate Transportation lines</AlertTitle>
              <AlertDescription>Remove the duplicate line before recalculating mileage.</AlertDescription>
            </Alert>
          ) : null}
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        {item ? (
          result?.complete ? (
            <Button type="button" onClick={handleApply} disabled={disabled || existingTransportationLines.length > 1}>
              {result.billableMiles <= 0 && existingTransportationLines.length > 0
                ? 'Remove Transportation Charge'
                : result.billableMiles <= 0
                  ? 'Done'
                  : existingTransportationLines.length > 0
                    ? 'Apply Recalculation'
                    : 'Add Transportation'}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleCalculate}
              disabled={disabled || calculate.isPending || selectedEvents.length === 0}
            >
              {calculate.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Calculate Mileage
            </Button>
          )
        ) : null}
      </DialogFooter>
    </>
  )
}
